/**
 * Shared warehousing constants and response normalisers.
 *
 * Field names below mirror the deployed backend (`warehouse_dashboard_service.py`,
 * `warehouse_service.py`, `warehouse_bulk_upload_service.py`), which differs from the
 * original design doc in several places — normalising here keeps that divergence in one
 * file instead of spread across components.
 *
 * Near-leaf module: `moment` (already used app-wide) is the only import, so it still
 * cannot take part in an internal cycle.
 */
import moment from 'moment';

export const WAREHOUSING_ROUTE = '/warehousing';

export const MODEL_PER_ORDER = 'per_order_slab';
export const MODEL_COST_PLUS = 'cost_plus';

export const MODEL_LABELS = {
	[MODEL_PER_ORDER]: 'Per Order',
	[MODEL_COST_PLUS]: 'Cost Plus',
};

export const LINE_ITEM_MANPOWER = 'manpower';
export const LINE_ITEM_SHIPMENT = 'shipment';
export const LINE_ITEM_BAG = 'bag';

export const ENTRY_TYPE_OPTIONS = [
	{ label: 'Manpower', value: LINE_ITEM_MANPOWER },
	{ label: 'Shipments', value: LINE_ITEM_SHIPMENT },
	{ label: 'Bag Count', value: LINE_ITEM_BAG },
];

/** The two independent freeze locks a warehousing project carries. */
export const FREEZE_LOCK_REVENUE = 'revenue';
export const FREEZE_LOCK_COST = 'cost';

export const MONTHLY_STATUS_PENDING = 'pending';
export const MONTHLY_STATUS_ADDED = 'added';

export const AGING_ON_TIME = 'on_time';
export const AGING_LATE = 'late';

export const DAILY_TAB = 'daily';
export const MONTHLY_TAB = 'monthly';
export const FIXED_TAB = 'fixed';

/** Sentinel option that reveals the free-text "New Cost Head" field. */
export const ADD_COST_HEAD_OPTION = '__add_cost_head__';

/**
 * Standard cost heads offered by the Fixed and Monthly pickers.
 *
 * Nothing is seeded server-side — a project starts with an empty catalog and operations
 * add the heads that apply, so this is purely the picker's shortlist. Heads already on the
 * project are filtered out, and anything not listed goes in as a custom head.
 */
export const COST_HEAD_OPTIONS = [
	'Manpower - On-roll',
	'Manpower - Outsourced',
	'Rent',
	'Security',
	'IT Hardware',
	'Electricity',
	'Housekeeping',
	'Consumables',
	'Repairs & Maintenance',
	'Insurance',
	'Internet & Connectivity',
	'Statutory Compliance',
];

/**
 * Only overtime manpower types accept `hours` (TDD §10) — the server rejects hours on any
 * other type. The catalog row's `is_overtime_type` flag is authoritative when the API
 * sends it; older responses omit it, so fall back to the type name.
 */
export const isOvertimeManpowerType = (type) => {
	if (!type) return false;
	if (typeof type.is_overtime_type === 'boolean') return type.is_overtime_type;
	const name = String(type.type_name || '')
		.trim()
		.toLowerCase();
	return name.includes('overtime') || name === 'ot';
};

/**
 * Look a type up in the catalog by id and report whether it accepts hours.
 *
 * Closed by default: no type picked, an unknown id, or a catalog that has not loaded all
 * mean hours stay disabled. `warehouse_manpower_types` always returns `is_overtime_type`
 * as a real boolean, so the only way to open the field is to select a type the catalog
 * says is overtime.
 *
 * ids are compared as strings — a Select can hand back the value as either.
 */
export const acceptsHours = (manpowerTypes, typeId) => {
	if (typeId === undefined || typeId === null || typeId === '') return false;
	const match = (manpowerTypes || []).find((t) => String(t?.id) === String(typeId));
	return isOvertimeManpowerType(match);
};

/**
 * Exact CSV header rows the bulk-upload service requires (whole file rejected otherwise).
 *
 * PascalCase (`EntryDate`, not `entry_date`) so the header row a customer opens in Excel/
 * Sheets reads like a proper column title rather than a raw code identifier.
 *
 * `ManpowerType` is the type's NAME (e.g. "Regular", "Overtime"), not its database id —
 * the backend resolves it against the project's catalog by name, creating the name if the
 * project hasn't seen it before. An id column here would force whoever fills in the sheet
 * to already know an internal primary key, which isn't something a customer should ever
 * need to look up.
 */
/**
 * Grid spans for the Daily tab's upload panels. The panels that apply to a project vary
 * (Shipments does not exist for Cost Plus, Bag Count only with a Cross Dock charge), so the
 * row is divided between however many are shown instead of leaving a gap for a missing one.
 */
export const uploadPanelColSpan = (panelCount) => ({
	xs: 24,
	lg: panelCount <= 1 ? 24 : 12,
	xl: Math.floor(24 / Math.max(panelCount, 1)),
});

export const CSV_HEADERS = {
	[LINE_ITEM_MANPOWER]: ['EntryDate', 'ManpowerType', 'Cost', 'Headcount', 'Hours'],
	[LINE_ITEM_SHIPMENT]: ['EntryDate', 'ShipmentCount'],
	[LINE_ITEM_BAG]: ['EntryDate', 'BagCount'],
};

/**
 * Cost Plus projects' manpower template: the same columns with CostToCustomer
 * inserted after Cost. Only manpower carries a Cost Plus variant — shipments
 * and bags feed revenue, not cost, so they are unaffected by this PRD.
 */
export const MANPOWER_CSV_HEADERS_COST_PLUS = [
	'EntryDate',
	'ManpowerType',
	'Cost',
	'CostToCustomer',
	'Headcount',
	'Hours',
];

/**
 * Timestamps come back in UTC — audit and entry rows default to the database clock and
 * are stored naive — so they are shifted to IST for display, the same way the trip
 * screens do it (see ReplacementHistoryModal's IST_OFFSET_MINUTES).
 *
 * IST has never observed DST, so the fixed offset is exact.
 */
export const IST_OFFSET_MINUTES = 330; // UTC+5:30

export const formatIstDateTime = (value) => {
	if (!value) return '—';
	const utc = moment.utc(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
	if (!utc.isValid()) return String(value);
	return utc.utcOffset(IST_OFFSET_MINUTES).format('DD-MM-YYYY hh:mm A');
};

/**
 * Map a project-figures payload onto the shape the KPI tiles expect.
 * Used for both the per-project dashboard and each row of the portfolio response.
 */
export const normalizeProjectFigures = (figures) => {
	if (!figures) return null;
	return {
		revenue: figures.revenue,
		costExclMonthly: figures.cost_excl_monthly,
		costInclMonthly: figures.cost_incl_monthly,
		costActual: figures.cost_actual,
		marginAbs: figures.margin_abs,
		marginPct: figures.margin_pct,
		shipmentCount: figures.shipment_count,
	};
};

/**
 * Customer-view figures for one project row of the portfolio response.
 *
 * The portfolio page shows what the customer is billed, so a Cost Plus project reads
 * `cost_plus_views.customer` (its Cost to Customer walk). That view is the base walk
 * only, so additional charges and net ad-hoc adjustments are layered on top, exactly as
 * the project detail header does, and margin is recomputed on revenue so
 * revenue = cost + margin. Per Order projects carry no `cost_plus_views` and keep their
 * own figures.
 */
export const customerViewFigures = (project) => {
	const view = project?.cost_plus_views?.customer;
	if (!view) {
		return {
			revenue: Number(project?.revenue) || 0,
			cost: Number(project?.cost_incl_monthly) || 0,
			costExclMonthly: Number(project?.cost_excl_monthly) || 0,
			marginAbs: Number(project?.margin_abs) || 0,
			marginPct: Number(project?.margin_pct) || 0,
		};
	}
	const extras =
		(Number(project.mtd_additional_charges) || 0) +
		(Number(project.net_revenue_adjustment) || 0);
	const revenue = Number(view.revenue) + extras;
	const cost = Number(view.cost);
	const marginAbs = revenue - cost;
	return {
		revenue,
		cost,
		costExclMonthly: Number(project.cost_excl_monthly_to_customer) || 0,
		marginAbs,
		marginPct: revenue ? (marginAbs / revenue) * 100 : 0,
	};
};

/** Adds the customer-view figures to each portfolio project row for the table. */
export const withCustomerView = (projects) =>
	(projects || []).map((project) => {
		const view = customerViewFigures(project);
		return {
			...project,
			customer_revenue: view.revenue,
			customer_cost: view.cost,
			customer_margin_pct: view.marginPct,
		};
	});

/**
 * Portfolio totals, in the customer view: summed from each project's customer-view
 * figures rather than the backend's company-basis `portfolio` totals.
 */
export const normalizePortfolio = (payload) => {
	if (!payload) return null;
	const totals = payload.portfolio || {};
	const projects = payload.projects || [];

	const views = projects.map(customerViewFigures);
	const revenue = views.reduce((sum, v) => sum + v.revenue, 0);
	const costInclMonthly = views.reduce((sum, v) => sum + v.cost, 0);
	const costExclMonthly = views.reduce((sum, v) => sum + v.costExclMonthly, 0);
	const marginAbs = revenue - costInclMonthly;

	return {
		revenue,
		costInclMonthly,
		costExclMonthly,
		marginAbs,
		marginPct: revenue ? (marginAbs / revenue) * 100 : 0,
		shipmentCount: projects.reduce(
			(sum, project) => sum + (Number(project.shipment_count) || 0),
			0
		),
		projectCount: totals.project_count ?? projects.length,
	};
};

/**
 * Merge several days' slab_breakdown arrays (each from the backend's revenue engine,
 * see compute_per_order_breakdown) into one month-to-date table.
 *
 * Each day's rows already pick up exactly where the days before it left off — the
 * backend chains them via already_billed — so two rows for the same slab are always
 * a contiguous span, not an overlap: summing their shipment_count/amount and taking
 * the min from_count / max to_count reconstructs that full span correctly.
 *
 * Rows are grouped by (slab_min, slab_max, rate_per_order) since that triple
 * identifies one slab; sorted by slab_min so the table reads low-to-high like the
 * project's own slab configuration.
 */
export const mergeSlabBreakdowns = (dailyBreakdownArrays) => {
	const bySlab = new Map();

	(dailyBreakdownArrays || []).forEach((rows) => {
		(rows || []).forEach((row) => {
			const key = `${row.slab_min}-${row.slab_max}-${row.rate_per_order}`;
			const existing = bySlab.get(key);
			if (existing) {
				existing.shipment_count += Number(row.shipment_count) || 0;
				existing.amount += Number(row.amount) || 0;
				existing.from_count = Math.min(existing.from_count, row.from_count);
				existing.to_count = Math.max(existing.to_count, row.to_count);
			} else {
				bySlab.set(key, {
					slab_min: row.slab_min,
					slab_max: row.slab_max,
					rate_per_order: row.rate_per_order,
					shipment_count: Number(row.shipment_count) || 0,
					amount: Number(row.amount) || 0,
					from_count: row.from_count,
					to_count: row.to_count,
				});
			}
		});
	});

	return Array.from(bySlab.values()).sort((a, b) => a.slab_min - b.slab_min);
};
